import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCart, effPrice } from "@/context/CartContext";
import ProductCard from "@/components/store/ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export function Hero() {
  const { ct } = useCart();
  return (
    <section id="top" className="grid-pattern relative border-b border-neutral-800 py-24 md:py-32 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-4xl animate-reveal">
          <h1
            data-testid="hero-headline"
            className="font-disp text-4xl sm:text-6xl md:text-7xl font-extrabold uppercase leading-[0.9] text-neutral-50 tracking-tight"
          >
            {ct("hero.headline", "Precision Surron plates milled to fit.")}
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-neutral-300 max-w-2xl leading-relaxed">
            {ct("hero.subtext", "Fair prices for the quality, premium materials, and exact bolt-on fit for Surron and e-motos. Fully custom designs vented and made to order.")}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              data-testid="hero-shop-cta"
              href="#contact"
              className="text-sm font-semibold uppercase tracking-wider bg-white hover:bg-neutral-200 text-neutral-950 px-8 py-4 rounded shadow-[0_0_24px_rgba(255,255,255,0.3)] transition-colors"
            >
              {ct("hero.cta", "Configure & Pay Online")}
            </a>
            <span className="text-xs text-neutral-400 tracking-wide">{ct("hero.note", "Ships worldwide · Fair direct pricing")}</span>
          </div>
        </div>

        <div className="mt-16 border border-neutral-800 rounded-lg overflow-hidden bg-neutral-900/60 p-4">
          <div className="aspect-[21/9] w-full rounded relative overflow-hidden border border-neutral-800">
            <img
              src="https://images.pexels.com/photos/26954240/pexels-photo-26954240.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Precision CNC milled vented plate"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/40 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center text-center p-6">
              <div>
                <span className="text-xs uppercase tracking-widest text-white font-bold block mb-1">{ct("hero.banner_overline", "CNC Mill Run 004")}</span>
                <span className="text-sm text-neutral-300 max-w-md block">
                  {ct("hero.banner_text", "High-airflow vented front plate geometry for Surron Light Bee X & Ultra Bee chassis")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ChaptersSection() {
  const { ct } = useCart();
  const chapters = ct("chapters", [
    { label: "Chapter I", title: "Laser-Vented Geometry", body: "Engineered heat dissipation profiles designed to preserve front electronics and battery bay cooling during high-load freeride." },
    { label: "Chapter II", title: "Zero-Slop Tolerances", body: "Direct factory mounting pitch without rattling or zip-tie hacks. Every plate aligns perfectly to stock and aftermarket triple clamps." },
    { label: "Chapter III", title: "Bespoke Custom Cut", body: "Upload rider numbers, custom text cutouts, or custom vent motifs. Each custom order is milled individually per specification." },
  ]);
  return (
    <section className="border-b border-neutral-800 py-16 px-6 bg-neutral-900/40">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        {chapters.map((ch, i) => (
          <div key={ch.label || i} className={`border-l-2 ${i === 0 ? "border-white" : "border-neutral-700"} pl-6`}>
            <span className="text-xs uppercase tracking-widest text-white font-bold">{ch.label}</span>
            <h3 className="text-base font-bold text-neutral-100 mt-2">{ch.title}</h3>
            <p className="text-xs text-neutral-400 mt-1 leading-relaxed">{ch.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function CatalogSection() {
  const { products, ct } = useCart();
  return (
    <section id="inventory" className="py-24 px-6 border-b border-neutral-800">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="text-xs uppercase tracking-widest text-white font-semibold">{ct("catalog.overline", "Hardware Lineup")}</span>
            <h2 className="font-disp text-3xl sm:text-4xl font-extrabold uppercase text-neutral-50 mt-1">{ct("catalog.heading", "Ready to Order")}</h2>
          </div>
          <p className="text-xs text-neutral-400 mt-2 md:mt-0 max-w-sm">
            {ct("catalog.subtext", "Fair pricing for genuine high-durability aircraft alloy and composite hardware.")}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => (
            <ProductCard key={p.product_id} product={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function MaterialsSection() {
  const { ct } = useCart();
  const specs = ct("materials.specs", [
    { label: "Material Grade", value: "6061-T6 Aluminum / High-Impact Polymer", highlight: false },
    { label: "Vent Surface Ratio", value: "+34% Airflow over solid plates", highlight: true },
    { label: "Hardware Included", value: "Black M5 Stainless Bolts + Spacers", highlight: false },
  ]);
  const fitment = ct("materials.fitment", [
    { model: "Surron Light Bee X", fit: "Direct Bolt-On", note: "Stock & Fastace/Fox forks" },
    { model: "Surron Ultra Bee", fit: "Direct Bolt-On", note: "Dedicated wide clamp" },
    { model: "Talaria Sting R / MX4", fit: "Adapter Kit Ready", note: "Universal mount kit" },
    { model: "Custom E-Scooters", fit: "Custom Machined", note: "Deck & fender specs" },
  ]);
  return (
    <section id="materials" className="py-20 px-6 border-b border-neutral-800 bg-neutral-900/20">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <span className="text-xs uppercase tracking-widest text-white font-bold">{ct("materials.overline", "Engineering Standard")}</span>
          <h2 className="font-disp text-2xl sm:text-3xl font-extrabold uppercase text-neutral-50 mt-2">{ct("materials.heading", "Exact Mount Geometry.")}</h2>
          <p className="text-sm text-neutral-300 mt-4 leading-relaxed">
            {ct("materials.body", "No wobbly universal brackets. Every cut file matches OEM Surron Light Bee, Ultra Bee, Storm Bee, and popular hyper-scooter headtubes down to the tenth of a millimeter.")}
          </p>
          <div className="mt-8 space-y-3 font-mono text-xs">
            {specs.map((s, i) => (
              <div key={s.label || i} className="flex items-center justify-between p-3 bg-neutral-900 border border-neutral-800 rounded">
                <span className="text-neutral-400">{s.label}</span>
                <span className="text-neutral-100 font-bold">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="p-6 bg-neutral-900 border border-neutral-800 rounded-lg">
            <div className="flex items-center justify-between mb-4 border-b border-neutral-800 pb-3">
              <span className="text-xs font-mono text-white">{ct("materials.fitment_title", "FITMENT COMPATIBILITY MATRIX")}</span>
            </div>
            <div className="space-y-2 font-mono text-xs" data-testid="fitment-matrix">
              {fitment.map((f, i) => (
                <div key={f.model || i} className="grid grid-cols-3 gap-2 p-2 bg-neutral-950 rounded text-neutral-300">
                  <span className="font-bold text-neutral-100">{f.model}</span>
                  <span className="text-white">{f.fit}</span>
                  <span className="text-neutral-400 text-right">{f.note}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ReviewsSection() {
  const { ct } = useCart();
  const reviews = ct("reviews.items", [
    { quote: "The vented plate cut my controller bay temps visibly on hot trail runs. Bolt holes lined up 100% first try on my Light Bee.", name: "Marcus K.", role: "Surron LBX Rider" },
    { quote: "Wheelie bar saved my rear subframe twice at the lot. Solid weld joints, fair price, came with all the mounting hardware.", name: "Devon R.", role: "Freeride Stunt" },
  ]);
  return (
    <section id="reviews" className="py-20 px-6 border-b border-neutral-800">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <span className="text-xs uppercase tracking-widest text-white font-bold">{ct("reviews.overline", "Rider Community")}</span>
          <h2 className="font-disp text-2xl sm:text-3xl font-extrabold uppercase text-neutral-50 mt-1">{ct("reviews.heading", "Field Tested in the Streets")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <div key={r.name || i} className="p-6 bg-neutral-900/70 border border-neutral-800 rounded">
              <p className="text-sm text-neutral-200 leading-relaxed">"{r.quote}"</p>
              <div className="mt-4 pt-4 border-t border-neutral-800 flex items-center justify-between text-xs">
                <span className="font-bold text-neutral-100">{r.name}</span>
                <span className="text-white font-mono">{r.role}</span>
              </div>
            </div>
          ))}
          <div className="p-6 bg-neutral-900/40 border border-dashed border-neutral-800 rounded flex flex-col justify-between text-neutral-400">
            <div>
              <span className="text-xs font-mono text-white uppercase">{ct("reviews.submit_title", "Submit Build Photos")}</span>
              <p className="text-xs text-neutral-400 mt-2">
                {ct("reviews.submit_body", "Tag your custom build with Gridline plates and wheelie bars to be featured in the rider gallery.")}
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-neutral-800 text-[11px] font-mono text-neutral-500">
              {ct("reviews.submit_footer", "Ships worldwide to all riders")}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  const { ct } = useCart();
  const faqs = ct("faq.items", [
    { q: "Do you ship worldwide?", a: "Yes, we ship anywhere worldwide with tracked international parcel delivery." },
    { q: "How do custom design orders work?", a: "Select the custom plate tier in the checkout form below, add your rider number or text notes, and we cut your plate to order." },
    { q: "What is included with the wheelie bars?", a: "Every wheelie bar kit includes the direct-mount bracket, grade 10.9 high-tensile hardware, and replaceable scrape pucks." },
  ]);
  return (
    <section id="faq" className="py-20 px-6 border-b border-neutral-800 bg-neutral-900/20">
      <div className="max-w-4xl mx-auto">
        <span className="text-xs uppercase tracking-widest text-white font-bold">{ct("faq.overline", "Frequently Asked")}</span>
        <h2 className="font-disp text-2xl sm:text-3xl font-extrabold uppercase text-neutral-50 mt-1 mb-8">{ct("faq.heading", "Order & Shipping FAQ")}</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={f.q || i} className="p-4 bg-neutral-900 border border-neutral-800 rounded">
              <h3 className="text-sm font-bold text-neutral-100">{f.q}</h3>
              <p className="text-xs text-neutral-400 mt-1">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CheckoutSection() {
  const { items, productMap, subtotal, orderNote, setOrderNote, ct } = useCart();
  const [name, setName] = useState("");
  const [bikeModel, setBikeModel] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const discount = promo ? Math.round(subtotal * (promo.percent / 100)) : 0;
  const total = subtotal - discount;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const { data } = await axios.get(`${API}/promos/validate`, { params: { code: promoInput.trim() } });
      setPromo(data);
      toast.success(`Code ${data.code} applied — ${data.percent}% off`);
    } catch (err) {
      setPromo(null);
      setPromoError(err.response?.data?.detail || "Invalid code");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCheckout = async (e, provider) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty — add a product first");
      return;
    }
    setLoading(provider);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items,
        email: email || undefined,
        custom_details: [bikeModel && `Bike: ${bikeModel}`, orderNote].filter(Boolean).join(" | ") || undefined,
        promo_code: promo?.code || undefined,
        provider,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-neutral-950 relative">
      <div className="max-w-3xl mx-auto">
        <div className="border border-white/40 rounded-xl p-8 bg-neutral-900/80 shadow-[0_0_40px_rgba(255,255,255,0.1)]">
          <div className="flex items-center justify-between border-b border-neutral-800 pb-6 mb-6">
            <div>
              <span className="text-xs uppercase tracking-widest text-white font-bold">{ct("checkout.overline", "Online Checkout")}</span>
              <h2 className="font-disp text-2xl sm:text-3xl font-extrabold uppercase text-neutral-50 mt-1">{ct("checkout.heading", "Place Your Hardware Order")}</h2>
            </div>
            <span className="text-xs font-mono px-2.5 py-1 bg-white/10 text-white border border-white/30 rounded">SECURE PAYMENT</span>
          </div>

          <div data-testid="checkout-summary" className="mb-6">
            {items.length === 0 ? (
              <p className="text-center text-xs font-mono uppercase tracking-widest text-neutral-500 py-4 border border-dashed border-neutral-800 rounded">
                {ct("checkout.empty_cart", "No items yet — hit Buy on any part above")}
              </p>
            ) : (
              <div className="border border-neutral-800 rounded divide-y divide-neutral-800">
                {items.map((item) => {
                  const p = productMap[item.product_id];
                  if (!p) return null;
                  return (
                    <div key={item.product_id} className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="text-neutral-200">
                        {p.name} <span className="text-neutral-500 font-mono">×{item.quantity}</span>
                      </span>
                      <span className="font-mono text-white">{fmt(effPrice(p) * item.quantity)}</span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-mono uppercase text-neutral-400">Subtotal</span>
                  <span data-testid="checkout-subtotal" className="font-mono text-lg font-bold text-white">{fmt(subtotal)}</span>
                </div>
                {promo && (
                  <div className="flex items-center justify-between px-4 py-3" data-testid="checkout-discount-row">
                    <span className="text-xs font-mono uppercase text-neutral-400">Code {promo.code} (−{promo.percent}%)</span>
                    <span className="font-mono text-white">−{fmt(discount)}</span>
                  </div>
                )}
                {promo && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-xs font-mono uppercase text-neutral-400">Total</span>
                    <span data-testid="checkout-total" className="font-mono text-lg font-bold text-white">{fmt(total)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <form data-testid="checkout-form" className="space-y-4" onSubmit={handleCheckout}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="buyer-name" className="block text-xs font-mono uppercase text-neutral-300 mb-1">Your Full Name</label>
                <input
                  id="buyer-name"
                  data-testid="checkout-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Rider"
                  className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label htmlFor="bike-model" className="block text-xs font-mono uppercase text-neutral-300 mb-1">Your Bike / Scooter Model</label>
                <input
                  id="bike-model"
                  data-testid="checkout-bike-model-input"
                  type="text"
                  value={bikeModel}
                  onChange={(e) => setBikeModel(e.target.value)}
                  placeholder="e.g. Surron LBX 2024 / Ultra Bee"
                  className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none transition-colors"
                />
              </div>
            </div>
            <div>
              <label htmlFor="checkout-email" className="block text-xs font-mono uppercase text-neutral-300 mb-1">Email for Tracking & Receipt</label>
              <input
                id="checkout-email"
                data-testid="checkout-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alex@domain.com"
                className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor="custom-details" className="block text-xs font-mono uppercase text-neutral-300 mb-1">
                Custom Text / Number / Shipping Notes
              </label>
              <textarea
                id="custom-details"
                data-testid="checkout-custom-details-input"
                rows={3}
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                placeholder="Enter custom plate text/number if ordering custom, and destination country/city for shipping"
                className="w-full bg-neutral-950 border border-neutral-800 rounded p-3 text-sm text-neutral-100 focus:border-white focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor="promo-code" className="block text-xs font-mono uppercase text-neutral-300 mb-1">Discount Code</label>
              <div className="flex gap-2">
                <input
                  id="promo-code"
                  data-testid="checkout-promo-input"
                  type="text"
                  value={promoInput}
                  onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(null); }}
                  placeholder="e.g. WELCOME10"
                  className="flex-1 bg-neutral-950 border border-neutral-800 rounded p-3 text-sm font-mono uppercase text-neutral-100 focus:border-white focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  data-testid="checkout-promo-apply"
                  onClick={applyPromo}
                  disabled={promoLoading}
                  className="px-5 bg-neutral-800 hover:bg-white hover:text-neutral-950 disabled:opacity-50 text-xs font-bold uppercase tracking-wider rounded transition-colors"
                >
                  {promoLoading ? "…" : "Apply"}
                </button>
              </div>
              {promoError && <p data-testid="promo-error" className="text-xs font-mono text-red-400 mt-1">{promoError}</p>}
              {promo && <p data-testid="promo-applied" className="text-xs font-mono text-white mt-1">{promo.code} applied — {promo.percent}% off</p>}
            </div>
            <div className="pt-4 space-y-3">
              <button
                data-testid="checkout-square-button"
                type="button"
                onClick={(e) => handleCheckout(e, "square")}
                disabled={!!loading}
                className="w-full py-4 bg-white hover:bg-neutral-200 disabled:opacity-50 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-colors"
              >
                {loading === "square" ? "Opening Secure Checkout…" : "Pay with Card / Apple Pay / Cash App"}
              </button>
              <button
                data-testid="checkout-paypal-button"
                type="button"
                onClick={(e) => handleCheckout(e, "paypal")}
                disabled={!!loading}
                className="w-full py-4 bg-[#FFC439] hover:bg-[#f5b82e] disabled:opacity-50 text-neutral-950 font-bold uppercase tracking-wider text-sm rounded transition-colors"
              >
                {loading === "paypal" ? "Opening PayPal…" : "Pay with PayPal"}
              </button>
              <p className="text-[11px] font-mono text-center text-neutral-400">
                We never share your details · Global tracked dispatch
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const { ct } = useCart();
  return (
    <footer className="border-t border-neutral-800 bg-neutral-950 py-12 px-6 text-xs text-neutral-400">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-white rounded" />
          <span className="font-bold text-neutral-200 uppercase tracking-wider">{ct("brand.name", "Gridline Machined Plates")}</span>
        </div>
        <div className="flex items-center gap-6 uppercase tracking-wider text-[11px]">
          <a href="#inventory" className="hover:text-white transition-colors">Parts</a>
          <a href="#materials" className="hover:text-white transition-colors">Specs</a>
          <a href="#reviews" className="hover:text-white transition-colors">Riders</a>
          <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          <a href="#contact" className="hover:text-white transition-colors">Order Online</a>
        </div>
        <div className="font-mono text-neutral-500">{ct("footer.tagline", "All-world shipping · Custom e-moto parts")}</div>
      </div>
    </footer>
  );
}
